import { useForm, Controller } from "react-hook-form";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "@/core/components/tooltip/Tooltip";
import { OrganizationDetails, ORGANIZATION_TYPES } from "../../model/types";
import { $organizationCategoriesTerms } from "@/core/store";
import { useUnit } from "effector-react";
import { useMemo } from "react";
import { Term } from "@/core/store/terms/types";

interface OrganizationDetailsStepProps {
  countryOptions: Array<{ label: string; value: string; tid: string }>;
  countriesLoading: boolean;
  showSkipOptions: boolean;
  onSubmit: (data: OrganizationDetails) => void;
  onDoItLater: () => void;
}

interface FormData {
  field_pic_number?: string;
  title: string;
  field_legal_form: string;
  field_country: string;
  field_type: string;
}

export const OrganizationDetailsStep = ({
  countryOptions,
  countriesLoading,
  showSkipOptions,
  onSubmit,
  onDoItLater,
}: OrganizationDetailsStepProps) => {
  const fundingTypeTerms = useUnit($organizationCategoriesTerms);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>();

  const handleFormSubmit = (data: FormData) => {
    const organizationDetails: OrganizationDetails = {
      field_pic_number: data.field_pic_number || "",
      title: data.title,
      field_legal_form: data.field_legal_form,
      field_country: data.field_country,
      field_type: [data.field_type],
    };
    onSubmit(organizationDetails);
  };

  const fundingTypeOptions = useMemo(() => {
    return fundingTypeTerms.data.map((term: Term) => ({
      label: term.name,
      value: term.name,
      tid: term.tid,
    }));
  }, [fundingTypeTerms.data]);

  return (
    <div className="step-content step-6">
      <div className="step-header">
        <p className="step-subtitle">Create your organisation</p>
        <h2 className="step-title">Fill in your organisation details</h2>
      </div>

      <form className="org-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="form-group">
          <label className="form-label">PIC number (optional)</label>
          <div className="input-with-help">
            <InputText
              placeholder="Your unique 9-digit identifier"
              className={`form-input-nopadding ${errors.field_pic_number ? "error" : ""}`}
              {...register("field_pic_number", {
                pattern: {
                  value: /^\d{9}$/,
                  message: "PIC number must be a 9-digit string",
                },
              })}
            />
            {errors.field_pic_number && (
              <span className="error-message">
                {errors.field_pic_number.message}
              </span>
            )}
            <Tooltip
              text="Participant Identification Code Number"
              position="top"
            >
              <i className="pi pi-question-circle help-icon"></i>
            </Tooltip>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Organisation name</label>
          <InputText
            placeholder="Organisation name"
            className={`form-input-nopadding ${errors.title ? "error" : ""}`}
            {...register("title", {
              required: "Organisation name is required",
            })}
          />
          {errors.title && (
            <span className="error-message">{errors.title.message}</span>
          )}
        </div>

        <div className="field-type-row">
          <div className="form-group type-field">
            <label className="form-label">Type</label>
            <Controller
              name="field_type"
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  options={fundingTypeOptions}
                  onChange={(e) => field.onChange(e.value)}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select type"
                  className={`form-input-nopadding ${errors.field_type ? "error" : ""}`}
                  panelClassName="account-setup-dropdown-panel"
                />
              )}
            />
            {errors.field_type && (
              <span className="error-message">{errors.field_type.message}</span>
            )}
          </div>
          <div className="form-group legal-form-field">
            <label className="form-label">Legal form</label>
            <Controller
              name="field_legal_form"
              control={control}
              rules={{ required: "Legal form is required" }}
              render={({ field }) => (
                <div className="legal-form-options">
                  <label
                    className={`legal-form-option ${field.value === "Public" ? "selected" : ""}`}
                  >
                    <span className="radio-circle">
                      <span
                        className={`radio-inner ${field.value === "Public" ? "checked" : ""}`}
                      ></span>
                    </span>
                    <input
                      type="radio"
                      name="field_legal_form"
                      value="Public"
                      checked={field.value === "Public"}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <span className="radio-label">Public</span>
                  </label>
                  <label
                    className={`legal-form-option ${field.value === "Private" ? "selected" : ""}`}
                  >
                    <span className="radio-circle">
                      <span
                        className={`radio-inner ${field.value === "Private" ? "checked" : ""}`}
                      ></span>
                    </span>
                    <input
                      type="radio"
                      name="field_legal_form"
                      value="Private"
                      checked={field.value === "Private"}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <span className="radio-label">Private</span>
                  </label>
                </div>
              )}
            />
            {errors.field_legal_form && (
              <span className="error-message">
                {errors.field_legal_form.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Country</label>
          <Controller
            name="field_country"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field }) => (
              <Dropdown
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={countryOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={
                  countriesLoading ? "Loading countries..." : "Select country"
                }
                className={`form-input-nopadding ${errors.field_country ? "error" : ""}`}
                disabled={countriesLoading}
                panelClassName="account-setup-dropdown-panel"
              />
            )}
          />
          {errors.field_country && (
            <span className="error-message">
              {errors.field_country.message}
            </span>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">
            Continue
          </button>
          {showSkipOptions && (
            <button type="button" className="skip-button" onClick={onDoItLater}>
              Do it later
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
